import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { patchProfile } from '../api/profileApi';
import { prepareRegisterRequestBody } from '../api/assetPayloadNormalize';
import { persistProfileContactHints } from '../utils/profileContactStorage';
import { persistProfileEditSavedFields } from '../utils/profileEditSave';
import { persistLocalBookingProfileFromWire } from '../utils/profileBookingCompleteStorage';
import {
  isProfilePatchIdentityConflictError,
  isProfilePatchUnknownGeoError,
  omitCompletionGeoFromPatch,
  signedInProfileCompletionPatchAttempts,
} from './profileCompletionPatch';
import { recordProfileUpdatedFromPatchResponse } from './profileLastUpdated';

export type ProfileCompletionPatchResult = {
  savedToServer: boolean;
  identityConflict: boolean;
};

export function persistSignedInProfileCompletionLocally(payload: Record<string, unknown>): void {
  const loginId = getStoredLoginIdentifier();
  persistProfileContactHints(
    typeof payload.email === 'string' ? payload.email : null,
    typeof payload.phone_number === 'string' ? payload.phone_number : null,
    loginId,
  );
  persistProfileEditSavedFields({
    firstName: typeof payload.first_name === 'string' ? payload.first_name : '',
    lastName: typeof payload.last_name === 'string' ? payload.last_name : '',
    occupation: typeof payload.occupation === 'string' ? payload.occupation : '',
    birthDate: typeof payload.birth_date === 'string' ? payload.birth_date : '',
  });
  persistLocalBookingProfileFromWire(payload, loginId);
}

/** PATCH /profile for booking completion with fallbacks when email/phone trigger 409. */
export async function patchSignedInProfileCompletion(
  payload: Record<string, unknown>,
): Promise<ProfileCompletionPatchResult> {
  const loginId = getStoredLoginIdentifier();
  const attempts = signedInProfileCompletionPatchAttempts(payload);
  let sawIdentityConflict = false;

  for (const attempt of attempts) {
    let body = attempt.body;
    for (let geoPass = 0; geoPass < 2; geoPass += 1) {
      try {
        const response = await patchProfile(prepareRegisterRequestBody(body));
        recordProfileUpdatedFromPatchResponse(response, loginId);
        return { savedToServer: true, identityConflict: sawIdentityConflict };
      } catch (err) {
        if (isProfilePatchIdentityConflictError(err)) {
          sawIdentityConflict = true;
          break;
        }
        if (
          geoPass === 0 &&
          isProfilePatchUnknownGeoError(err) &&
          (body.country_id != null || body.state_id != null)
        ) {
          body = omitCompletionGeoFromPatch(body);
          continue;
        }
        throw err;
      }
    }
  }

  return { savedToServer: false, identityConflict: sawIdentityConflict };
}
